import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  InputAdornment,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, Category as CategoryIcon, Visibility } from '@mui/icons-material';
import { chamadoAssuntoService, ChamadoAssunto } from '../../services/chamadoAssuntoService';
import { chamadoCategoriaService, ChamadoCategoria } from '../../services/chamadoCategoriaService';
import { toast } from 'react-toastify';

interface ParametroChamados {
  auto_atribuicao: boolean;
  notificacao_email: boolean;
  notificacao_sms: boolean;
  tempo_sla: number;
  categorias_padrao: string[];
}

interface ParametrosChamadosProps {
  parametros: ParametroChamados;
  setParametros: React.Dispatch<React.SetStateAction<ParametroChamados>>;
}

const ParametrosChamados: React.FC<ParametrosChamadosProps> = ({
  parametros,
  setParametros,
}) => {
  const [tipos, setTipos] = useState<ChamadoAssunto[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);

  // Estados para Categorias
  const [categorias, setCategorias] = useState<ChamadoCategoria[]>([]);
  const [modalCategoriasAberto, setModalCategoriasAberto] = useState(false);
  const [editandoCategoria, setEditandoCategoria] = useState<ChamadoCategoria | null>(null);
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [corCategoria, setCorCategoria] = useState('#2196f3');
  const [salvandoCategoria, setSalvandoCategoria] = useState(false);

  // Estados para Gerenciamento de Assuntos dentro da Categoria
  const [modalAssuntosPorCategoriaAberto, setModalAssuntosPorCategoriaAberto] = useState(false);
  const [categoriaAtivaParaAssuntos, setCategoriaAtivaParaAssuntos] = useState<ChamadoCategoria | null>(null);
  const [novoAssuntoNomeRapido, setNovoAssuntoNomeRapido] = useState('');
  const [salvandoAssuntoRapido, setSalvandoAssuntoRapido] = useState(false);

  // Estados para Edição de Assunto
  const [modalEditarAssuntoAberto, setModalEditarAssuntoAberto] = useState(false);
  const [assuntoSendoEditado, setAssuntoSendoEditado] = useState<ChamadoAssunto | null>(null);
  const [nomeAssuntoEditando, setNomeAssuntoEditando] = useState('');
  const [categoriaIdAssuntoEditando, setCategoriaIdAssuntoEditando] = useState<number | string>('');
  const [salvandoEdicaoAssunto, setSalvandoEdicaoAssunto] = useState(false);

  const carregarTipos = async () => {
    try {
      setLoadingTipos(true);
      const res = await chamadoAssuntoService.getAll();
      if (res.success && res.data) {
        setTipos(res.data);
      }
    } catch {
      toast.error('Erro ao carregar tipos de chamados');
    } finally {
      setLoadingTipos(false);
    }
  };

  const carregarCategorias = async () => {
    try {
      const res = await chamadoCategoriaService.getAll();
      if (res.success && res.data) {
        setCategorias(res.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  useEffect(() => {
    carregarTipos();
    carregarCategorias();
  }, []);

  const handleExcluirTipo = async (id: number) => {
    const tipo = tipos.find((t) => t.id === id);
    if (tipo?.padrao) return;
    try {
      setExcluindoId(id);
      const res = await chamadoAssuntoService.delete(id);
      if (res.success) {
        setTipos((prev) => prev.filter((t) => t.id !== id));
        toast.success('Tipo excluído com sucesso');
      } else {
        toast.error(res.message || 'Erro ao excluir tipo');
      }
    } catch {
      toast.error('Erro ao excluir tipo');
    } finally {
      setExcluindoId(null);
    }
  };

  const handleSalvarCategoria = async () => {
    if (!nomeCategoria.trim()) return;
    try {
      setSalvandoCategoria(true);
      if (editandoCategoria) {
        const res = await chamadoCategoriaService.update(editandoCategoria.id, { nome: nomeCategoria, cor: corCategoria });
        if (res.success) {
          setCategorias(prev => prev.map(c => c.id === editandoCategoria.id ? res.data! : c));
          setEditandoCategoria(null);
          setNomeCategoria('');
          toast.success('Categoria atualizada');
        }
      } else {
        const res = await chamadoCategoriaService.create({ nome: nomeCategoria, cor: corCategoria });
        if (res.success) {
          setCategorias(prev => [...prev, res.data!]);
          setNomeCategoria('');
          toast.success('Categoria adicionada');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
    } finally {
      setSalvandoCategoria(false);
    }
  };

  const handleExcluirCategoria = async (id: number) => {
    try {
      const res = await chamadoCategoriaService.delete(id);
      if (res.success) {
        setCategorias(prev => prev.filter(c => c.id !== id));
        toast.success('Categoria excluída');
      }
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
    }
  };

  const handleAdicionarAssuntoRapido = async () => {
    if (!categoriaAtivaParaAssuntos || !novoAssuntoNomeRapido.trim()) return;
    try {
      setSalvandoAssuntoRapido(true);
      const res = await chamadoAssuntoService.create(novoAssuntoNomeRapido.trim(), categoriaAtivaParaAssuntos.id);
      if (res.success && res.data) {
        setTipos(prev => [...prev, res.data!]);
        setNovoAssuntoNomeRapido('');
        toast.success('Assunto adicionado');
      } else {
        toast.error(res.message || 'Erro ao adicionar assunto');
      }
    } catch (error) {
      console.error('Erro ao adicionar assunto:', error);
    } finally {
      setSalvandoAssuntoRapido(false);
    }
  };

  const handleAbrirEdicaoAssunto = (assunto: ChamadoAssunto) => {
    setAssuntoSendoEditado(assunto);
    setNomeAssuntoEditando(assunto.nome);
    setCategoriaIdAssuntoEditando(assunto.categoria_id || '');
    setModalEditarAssuntoAberto(true);
  };

  const handleSalvarEdicaoAssunto = async () => {
    if (!assuntoSendoEditado || !nomeAssuntoEditando.trim()) return;
    try {
      setSalvandoEdicaoAssunto(true);
      const res = await chamadoAssuntoService.update(
        assuntoSendoEditado.id,
        nomeAssuntoEditando.trim(),
        categoriaIdAssuntoEditando ? Number(categoriaIdAssuntoEditando) : null
      );
      if (res.success && res.data) {
        setTipos(prev => prev.map(t => t.id === assuntoSendoEditado.id ? res.data! : t));
        setModalEditarAssuntoAberto(false);
        setAssuntoSendoEditado(null);
        toast.success('Assunto atualizado');
      } else {
        toast.error(res.message || 'Erro ao atualizar assunto');
      }
    } catch (error) {
      console.error('Erro ao salvar edição do assunto:', error);
    } finally {
      setSalvandoEdicaoAssunto(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Configurações de Chamados
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tempo SLA"
            type="number"
            value={parametros.tempo_sla}
            onChange={(e) => setParametros(prev => ({ ...prev, tempo_sla: parseInt(e.target.value) || 0 }))}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">horas</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Categorias Padrão
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {parametros.categorias_padrao.map((categoria, index) => (
                <Chip
                  key={index}
                  label={categoria}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.auto_atribuicao}
                onChange={(e) => setParametros(prev => ({ ...prev, auto_atribuicao: e.target.checked }))}
                color="primary"
              />
            }
            label="Auto Atribuição"
          />
          <Typography variant="caption" display="block" color="text.secondary">
            Atribui chamados automaticamente aos técnicos disponíveis
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.notificacao_email}
                onChange={(e) => setParametros(prev => ({ ...prev, notificacao_email: e.target.checked }))}
                color="success"
              />
            }
            label="Notificações por E-mail"
          />
          <Typography variant="caption" display="block" color="text.secondary">
            Envia notificações por e-mail para novos chamados
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={parametros.notificacao_sms}
                onChange={(e) => setParametros(prev => ({ ...prev, notificacao_sms: e.target.checked }))}
                color="info"
              />
            }
            label="Notificações por SMS"
          />
          <Typography variant="caption" display="block" color="text.secondary">
            Envia notificações por SMS para chamados urgentes
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<CategoryIcon />}
              onClick={() => setModalCategoriasAberto(true)}
            >
              Gerenciar Categorias e Assuntos
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Modal de Gerenciamento de Categorias */}
      <Dialog
        open={modalCategoriasAberto}
        onClose={() => setModalCategoriasAberto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1.1rem' }}>Gerenciar Categorias e Assuntos</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Nova Categoria"
              value={nomeCategoria}
              onChange={(e) => setNomeCategoria(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <TextField
              size="small"
              type="color"
              label="Cor"
              value={corCategoria}
              onChange={(e) => setCorCategoria(e.target.value)}
              sx={{ width: 80 }}
            />
            <Button
              variant="contained"
              onClick={handleSalvarCategoria}
              disabled={salvandoCategoria || !nomeCategoria.trim()}
              size="small"
            >
              {editandoCategoria ? 'Salvar' : 'Add'}
            </Button>
            {editandoCategoria && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => { setEditandoCategoria(null); setNomeCategoria(''); setCorCategoria('#2196f3'); }}
              >
                Cancelar
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ height: "400px", maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="center">Cor</TableCell>
                  <TableCell align="center">Assuntos</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categorias.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell>{cat.nome}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 20, height: 20, bgcolor: cat.cor, borderRadius: '50%', mx: 'auto' }} />
                    </TableCell>
                    <TableCell align="center">
                      {tipos.filter(t => t.categoria_id === cat.id).length}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Gerenciar Assuntos"
                        onClick={() => { setCategoriaAtivaParaAssuntos(cat); setModalAssuntosPorCategoriaAberto(true); }}
                      >
                        <Visibility fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditandoCategoria(cat); setNomeCategoria(cat.nome); setCorCategoria(cat.cor || '#2196f3'); }}>
                        <Edit fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleExcluirCategoria(cat.id)}>
                        <Delete fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {categorias.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>Nenhuma categoria cadastrada</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ py: 1, px: 2 }}>
          <Button size="small" onClick={() => setModalCategoriasAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Gerenciamento de Assuntos por Categoria */}
      <Dialog
        open={modalAssuntosPorCategoriaAberto}
        onClose={() => setModalAssuntosPorCategoriaAberto(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem' }}>Assuntos: {categoriaAtivaParaAssuntos?.nome}</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <TextField
              size="small"
              placeholder="Novo assunto..."
              fullWidth
              value={novoAssuntoNomeRapido}
              onChange={(e) => setNovoAssuntoNomeRapido(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarAssuntoRapido()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAdicionarAssuntoRapido}
              disabled={salvandoAssuntoRapido || !novoAssuntoNomeRapido.trim()}
            >
              {salvandoAssuntoRapido ? <CircularProgress size={18} /> : <Add />}
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small">
              <TableBody>
                {tipos
                  .filter(t => t.categoria_id === categoriaAtivaParaAssuntos?.id)
                  .map(assunto => (
                    <TableRow key={assunto.id} hover>
                      <TableCell>{assunto.nome}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleAbrirEdicaoAssunto(assunto)}>
                          <Edit fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleExcluirTipo(assunto.id)}>
                          <Delete fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {tipos.filter(t => t.categoria_id === categoriaAtivaParaAssuntos?.id).length === 0 && (
                  <TableRow>
                    <TableCell align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      Nenhum assunto nesta categoria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ py: 1, px: 2 }}>
          <Button size="small" onClick={() => setModalAssuntosPorCategoriaAberto(false)}>Voltar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição de Assunto */}
      <Dialog
        open={modalEditarAssuntoAberto}
        onClose={() => setModalEditarAssuntoAberto(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem' }}>Editar Assunto</DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          <TextField
            fullWidth
            label="Assunto"
            value={nomeAssuntoEditando}
            onChange={(e) => setNomeAssuntoEditando(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label="Categoria"
            value={categoriaIdAssuntoEditando}
            onChange={(e) => setCategoriaIdAssuntoEditando(e.target.value)}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="">Nenhuma</option>
            {categorias.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.nome}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ py: 1, px: 2 }}>
          <Button size="small" onClick={() => setModalEditarAssuntoAberto(false)} disabled={salvandoEdicaoAssunto}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSalvarEdicaoAssunto}
            disabled={salvandoEdicaoAssunto || !nomeAssuntoEditando.trim()}
          >
            {salvandoEdicaoAssunto ? <CircularProgress size={18} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParametrosChamados;
